import { Device } from "@/hooks/useDevices";
import StatusBadge from "../StatusBadge";
import DeviceActions from "./DeviceActions";
import { Calendar, Activity, Heart } from "lucide-react";
import { AppleDeviceIcon } from "@/components/icons/AppleDeviceIcon";
import { GlassCard, GlassCardContent } from "../ui/glass-card";
import { calculateDeviceHealthScore, getHealthScoreColor } from "@/utils/deviceHealth";
import { useMemo } from "react";
import { DeviceTagAssignment } from "./DeviceTagAssignment";
import { useSofaFeed } from "@/hooks/useSofaFeed";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { useIsMobile } from "@/hooks/use-mobile";

interface DeviceHeaderProps {
  device: Device;
  onCardClick?: (tab: string) => void;
}

const DeviceHeader = ({ device, onCardClick }: DeviceHeaderProps) => {
  const status = device.status as 'active' | 'inactive' | 'pending' | 'unenrolled';
  const { releases: sofaReleases } = useSofaFeed();
  const { formatDate } = useUserTimezone();
  const isMobile = useIsMobile();
  const healthScore = useMemo(() => calculateDeviceHealthScore(device, sofaReleases), [device, sofaReleases]);
  
  const scriptDeploymentsCount = device.script_deployments?.length || 0;

  const issuesCount = healthScore.metrics.filter(
    m => m.status === 'poor' || m.status === 'fair'
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AppleDeviceIcon osType={device.os_type || 'macOS'} modelName={device.name} className="h-8 w-8 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">{device.name || device.hostname}</h1>
              <StatusBadge status={status} />
            </div>
            {device.name !== device.hostname && (
              <p className="text-sm text-muted-foreground">{device.hostname}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Actions</span>
          <DeviceActions />
        </div>
      </div>

      <DeviceTagAssignment deviceId={device.id} />
      
      <div className={`grid grid-cols-3 gap-2 md:gap-4`}>
        {(() => {
          const cardKeyDown = (tab: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onCardClick?.(tab);
            }
          };
          const focusRing =
            "min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
          return (
            <>
              <GlassCard
                variant="subtle"
                hover={false}
                className={`cursor-pointer group ${focusRing}`}
                onClick={() => onCardClick?.('info')}
                role="button"
                tabIndex={0}
                aria-label={`Device status ${status}. View system information.`}
                onKeyDown={cardKeyDown('info')}
              >
                <GlassCardContent className={isMobile ? "p-3" : "p-6"}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1 truncate">Device Status</p>
                      <h3 className="text-base md:text-2xl font-bold text-foreground truncate">{status.charAt(0).toUpperCase() + status.slice(1)}</h3>
                      {!isMobile && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Last seen: {formatDate(device.last_seen || device.created_at)}
                        </p>
                      )}
                    </div>
                    {!isMobile && (
                      <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" aria-hidden="true">
                        <Activity className="h-5 w-5 text-primary" />
                      </div>
                    )}
                  </div>
                </GlassCardContent>
              </GlassCard>

              <GlassCard
                variant="subtle"
                hover={false}
                className={`cursor-pointer group ${focusRing}`}
                onClick={() => onCardClick?.('deployments')}
                role="button"
                tabIndex={0}
                aria-label={`Scheduled actions: ${scriptDeploymentsCount} pending ${scriptDeploymentsCount === 1 ? 'deployment' : 'deployments'}. View deployments.`}
                onKeyDown={cardKeyDown('deployments')}
              >
                <GlassCardContent className={isMobile ? "p-3" : "p-6"}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1 truncate">Scheduled</p>
                      <h3 className="text-base md:text-2xl font-bold text-foreground">{scriptDeploymentsCount}</h3>
                      {!isMobile && <p className="text-sm text-muted-foreground mt-2">Pending deployments</p>}
                    </div>
                    {!isMobile && (
                      <div className="w-8 h-8 rounded-lg bg-accent border border-accent/20 flex items-center justify-center shrink-0" aria-hidden="true">
                        <Calendar className="h-5 w-5 text-accent-foreground" />
                      </div>
                    )}
                  </div>
                </GlassCardContent>
              </GlassCard>

              <GlassCard
                variant="subtle"
                hover={false}
                className={`cursor-pointer group ${focusRing}`}
                onClick={() => onCardClick?.('analytics')}
                role="button"
                tabIndex={0}
                aria-label={`Health score ${healthScore.overall}, grade ${healthScore.grade}. ${issuesCount > 0 ? `${issuesCount} ${issuesCount === 1 ? 'issue needs' : 'issues need'} attention.` : 'All systems healthy.'} View analytics.`}
                onKeyDown={cardKeyDown('analytics')}
              >
                <GlassCardContent className={isMobile ? "p-3" : "p-6"}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1 truncate">Health</p>
                      <div className="flex items-baseline gap-1 md:gap-2 flex-wrap">
                        <h3 className="text-base md:text-2xl font-bold text-foreground">{healthScore.overall}</h3>
                        <span className="text-[10px] md:text-sm font-semibold px-1.5 md:px-2 py-0.5 rounded" style={{
                          backgroundColor: getHealthScoreColor(healthScore.overall) + '20',
                          color: getHealthScoreColor(healthScore.overall)
                        }}>
                          {isMobile ? healthScore.grade : `Grade ${healthScore.grade}`}
                        </span>
                      </div>
                      {!isMobile && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {issuesCount > 0 ? `${issuesCount} ${issuesCount === 1 ? 'issue' : 'issues'} need attention` : 'All systems healthy'}
                        </p>
                      )}
                    </div>
                    {!isMobile && (
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" aria-hidden="true" style={{
                        backgroundColor: `${getHealthScoreColor(healthScore.overall)}1A`,
                        borderColor: `${getHealthScoreColor(healthScore.overall)}33`
                      }}>
                        <Heart className="h-5 w-5" style={{ color: getHealthScoreColor(healthScore.overall) }} />
                      </div>
                    )}
                  </div>
                </GlassCardContent>
              </GlassCard>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default DeviceHeader;
